import { useState } from 'react';
import { Alert, Button, Form, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import { EntryLayout } from './EntryLayout';
import { recover } from '../api/pa';
import { errorText } from '../session/errorText';
import type { Navigation } from '../router';

/**
 * Password recovery (`ui-enduser-personal-area` §2; Appendix L §4 **F2**) — PUBLIC,
 * two steps, ROTATE-AND-SEND (customer decision **P7-A2**, 2026-08-11).
 *
 * The portal mints a new password and sends it to the named contact; the subscriber
 * reads it out of the SMS or the email and logs in with it, then changes it on §5. There
 * is no code entry and he does not choose the value — the three-step code exchange the
 * spec used to describe is struck there, kept because it is the intuitive design and
 * would otherwise be re-derived.
 *
 * ============================================================================
 * ⚠ TWO CONTROLS ARE DELIBERATELY ABSENT. Do not "restore" either of them.
 * ============================================================================
 *
 *  1. THE «ВОССТАНОВИТЬ ЧЕРЕЗ» RADIO that `ui-enduser-personal-area` §2 still shows in
 *     its Step-1 table *(not rendered, 2026-08-11, Phase 7 W1b)*. `POST /pa/auth/recover`
 *     takes `{login, contact}` and NOTHING ELSE: the channel is DERIVED from the contact
 *     the subscriber typed — an email address recovers by email, a phone number by SMS —
 *     and `internal/users`' rotation sends to that ONE matched contact, never to a second
 *     one. So a channel picker would be a control the subscriber can press with no
 *     effect on what the server does, which is precisely this project's D109/D211 class
 *     (a lever that cannot move anything, believed by whoever presses it). The spec's own
 *     cell already scopes it to «shown only when the entered contact supports more than
 *     one channel (a phone can be SMS or Messenger)» — and messenger delivery on this
 *     route does not exist in the engine. When it does, the radio comes back WITH it.
 *
 *  2. A CAPTCHA. §2 says «If captcha is enabled it must be passed first»; no captcha
 *     exists anywhere in this system. The route's actual protection is the durable
 *     per-source counter plus the in-process bucket, both in the server.
 *
 * ============================================================================
 * ⚠ ONE WORDING FOR EVERY OUTCOME — this is a security property, not copy
 * ============================================================================
 *
 * A match, an unknown login, a contact that is not on the card, an unconfirmed contact
 * and an archived subscriber ALL answer 200 and ALL render `recover.result`, unchanged
 * and with nothing interpolated into it. Appendix L §4 F2 struck its own «if the card has
 * no confirmed contact show the contact-centre message instead» branch on 2026-08-11 for
 * exactly this reason: it made the response vary by login, so an unauthenticated caller
 * learnt both that a login exists and whether it has a confirmed contact. The
 * contact-centre hint therefore lives INSIDE the single uniform text.
 *
 * `RecoverScreen.test.tsx` asserts the rendered output is byte-identical for a matching
 * and a non-matching request. A per-outcome message would fail it by name.
 *
 * The two refusals that ARE distinguishable come from the server and are about the
 * INSTALLATION or the CALLER's own address, never about a subscriber: `403
 * pa.recovery_disabled` (the operator switched self-service recovery off) and `429
 * pa.too_many_attempts`. They are shown exactly as the server worded them.
 */
export function RecoverScreen({ navigate }: { navigate: Navigation['navigate'] }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const submit = async (values: { login: string; contact: string }) => {
    setBusy(true);
    setError(null);
    try {
      await recover(values.login, values.contact);
      setSent(true);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const backLink = (
    <Button type="link" onClick={() => navigate({ kind: 'home' })} style={{ padding: 0 }}>
      {t('recover.back')}
    </Button>
  );

  if (sent) {
    return (
      <EntryLayout>
        <h2 className="pa-page-title">{t('recover.resultTitle')}</h2>
        {/* Nothing the caller typed is echoed here — see the header. */}
        <p className="pa-lead">{t('recover.result')}</p>
        <div className="pa-form-footer">{backLink}</div>
      </EntryLayout>
    );
  }

  return (
    <EntryLayout>
      <h2 className="pa-page-title">{t('recover.title')}</h2>
      <p className="pa-lead">{t('recover.lead')}</p>

      {error ? (
        <Alert type="error" showIcon message={errorText(error, t)} style={{ marginBottom: 20 }} />
      ) : null}

      <Form layout="vertical" requiredMark={false} onFinish={submit} disabled={busy}>
        <Form.Item
          name="login"
          label={t('recover.loginLabel')}
          rules={[{ required: true, message: t('common.required') }]}
        >
          <Input
            placeholder={t('recover.loginPlaceholder')}
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            size="large"
          />
        </Form.Item>

        <Form.Item
          name="contact"
          label={t('recover.contactLabel')}
          rules={[{ required: true, message: t('common.required') }]}
        >
          {/* Type `text`, not `email`: this one field accepts an email address OR a
              phone number (the server matches a number on its last ten digits), and an
              `email` input would refuse the phone half in the browser. */}
          <Input placeholder={t('recover.contactPlaceholder')} spellCheck={false} size="large" />
        </Form.Item>

        <Button type="primary" htmlType="submit" size="large" block loading={busy}>
          {t('recover.submit')}
        </Button>
      </Form>

      <div className="pa-form-footer">{backLink}</div>
    </EntryLayout>
  );
}
